/**
 * A voice call in the thread (RFC 0307 3c): what was said, both sides, in one
 * quiet card between the typed messages around it. Closed by default to one
 * line, because the thread is about the work and the call is how it was
 * asked for; open, it is the transcript, and a line the voice sent to the
 * agent says so, because that is the one line that started work.
 */

import { AudioLines, ChevronRight } from 'lucide-react'
import { useState } from 'react'
import type { SpokenLine } from './threadRows'

/** "Voice call · 6 lines · 2 sent to the agent" */
export function callSummary(lines: SpokenLine[]): string {
  const sent = lines.filter((l) => l.relayed).length
  return [
    'Voice call',
    `${lines.length} ${lines.length === 1 ? 'line' : 'lines'}`,
    ...(sent > 0 ? [`${sent} sent to the agent`] : []),
  ].join(' · ')
}

export function VoiceCallRow({
  lines,
  onOpenSession,
}: {
  lines: SpokenLine[]
  onOpenSession?: (sessionId: string) => void
}) {
  const [open, setOpen] = useState(false)
  return (
    <div className="rounded-xl border border-border/70 bg-muted/30 px-3.5 py-2 text-sm">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="flex w-full items-center gap-1.5 text-left text-muted-foreground text-xs hover:text-foreground"
      >
        <AudioLines className="size-3" strokeWidth={2.25} aria-hidden />
        <span>{callSummary(lines)}</span>
        <ChevronRight
          className={`size-3 transition-transform ${open ? 'rotate-90' : ''}`}
          strokeWidth={2.25}
          aria-hidden
        />
      </button>
      {open ? (
        <div className="mt-1.5 flex flex-col gap-1">
          {lines.map((line) => (
            <div key={line.key} className="flex gap-2 leading-6">
              <span className="w-10 shrink-0 text-muted-foreground text-xs leading-6">
                {line.by === 'person' ? 'You' : 'Voice'}
              </span>
              <span
                className={`min-w-0 break-words ${
                  line.by === 'person'
                    ? 'text-foreground'
                    : 'text-foreground/70'
                }`}
              >
                {line.text}
                {line.homeSessionId ? (
                  <button
                    type="button"
                    onClick={() =>
                      onOpenSession?.(line.homeSessionId as string)
                    }
                    disabled={!onOpenSession}
                    className="ml-2 text-muted-foreground text-xs hover:text-foreground disabled:cursor-default"
                  >
                    From voice call
                  </button>
                ) : null}
                {line.targetSessionId ? (
                  <button
                    type="button"
                    onClick={() =>
                      onOpenSession?.(line.targetSessionId as string)
                    }
                    disabled={!onOpenSession}
                    className="ml-2 text-muted-foreground text-xs hover:text-foreground disabled:cursor-default"
                  >
                    sent to session
                  </button>
                ) : line.relayed ? (
                  <span className="ml-2 whitespace-nowrap text-muted-foreground text-xs">
                    sent to the agent
                  </span>
                ) : null}
              </span>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  )
}
